const STORAGE_PREFIX = "chat-compact-playbook-dismissed";

const IS_SERVER = globalThis.window === undefined;

const storageKey = (chatSessionId: string): string => `${STORAGE_PREFIX}-${chatSessionId}`;

/**
 * Remembers that the broker chose the live transcript over the compact surface for a given run,
 * so the choice survives a reload. Compact mode is otherwise re-derived from the server on every
 * mount, which makes any misread of a run's liveness refresh-proof — the shape of the B-2012
 * report, where no number of refreshes got the chat back.
 *
 * Scoped to the run id, not just the session: the next playbook on the same session is a fresh
 * decision. `sessionStorage` alone (per tab, no `localStorage` seed) matches that lifetime, and
 * deliberately does not propagate the choice to other tabs.
 *
 * Storage access is guarded for the same reason {@link useTabScopedStorage} guards it — private
 * browsing, quota, and storage-disabled policies all throw on access. `getDismissedCompactRunId`
 * is read inside a `useState` lazy initializer, so an unguarded throw would surface as a render
 * error and be caught by the inbox's `RootErrorBoundary`: a blank shell during a live playbook,
 * which is the failure this surface exists to prevent. Losing the dismissal is the safe outcome.
 */
export const getDismissedCompactRunId = (chatSessionId: string): string | null => {
  if (IS_SERVER) {
    return null;
  }

  try {
    return globalThis.sessionStorage.getItem(storageKey(chatSessionId));
  } catch {
    return null;
  }
};

export const setDismissedCompactRunId = (chatSessionId: string, runId: string): void => {
  if (IS_SERVER) {
    return;
  }

  try {
    globalThis.sessionStorage.setItem(storageKey(chatSessionId), runId);
  } catch {
    // The dismissal is a convenience, not correctness: without it the broker re-clicks
    // "Show live transcript" after a reload. Never worth breaking the render over.
  }
};
